import React, { useCallback, useEffect, useMemo, useState } from "react";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SendIcon from "@mui/icons-material/Send";
import RefreshIcon from "@mui/icons-material/Refresh";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { api } from "../../../../api/apiClient";
import PageHeader from "../../../../app/layout/PageHeader";
import { notify } from "../../../components-ui/toast";
import { useAuth } from "../../../../context/AuthContext";
import "../../../../css/forms.css";
import "../../../../css/cards.css";
import "../../../../css/buttons.css";
import "../../../../css/layout.css";

interface CampaignRecipientPreviewDto {
  partyId: number;
  displayName?: string | null;
  recipientName?: string | null;
  mobileNumber?: string | null;
  phoneNumber?: string | null;
  destination?: string | null;
  personalisedMessage?: string | null;
  estimatedSegments?: number | null;
  warnings?: string[];
}

interface CampaignReviewDto {
  campaignId: number;
  campaignName: string;
  messageBody: string;
  senderName?: string;
  audienceCount: number;
  recipientCount: number;
  estimatedSegments: number;
  estimatedMessages: number;
  estimatedCost?: number;
  recipients: CampaignRecipientPreviewDto[];
  warnings: string[];
  serviceCommunicationType?: "PreRegistration" | "PostRegistration" | null;
  serviceName?: string | null;
  sessionStartDateTime?: string | null;
}

interface CampaignReviewViewProps {
  campaignId: number | null;
  onBack: () => void;
  onQueued?: () => void;
}

const formatNumber = (value?: number | null) =>
  new Intl.NumberFormat("en-GB").format(value ?? 0);

const formatCurrency = (value?: number) =>
  value === undefined || value === null
    ? "Not available"
    : new Intl.NumberFormat("en-GB", {
        style: "currency",
        currency: "GBP",
      }).format(value);

export default function CampaignReviewView({
  campaignId,
  onBack,
  onQueued,
}: CampaignReviewViewProps) {
  const { canSendCommunications } = useAuth();
  const [review, setReview] = useState<CampaignReviewDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [queueing, setQueueing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [previewIndex, setPreviewIndex] = useState(0);

  const loadReview = useCallback(async () => {
    if (!campaignId) {
      setReview(null);
      setError("Save the campaign before reviewing it.");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const data = await api<CampaignReviewDto>(
        `/api/campaigns/${campaignId}/review`,
      );
      setReview({
        ...data,
        recipients: data.recipients ?? [],
        warnings: data.warnings ?? [],
      });
      setPreviewIndex(0);
    } catch {
      setReview(null);
      setError("Failed to load campaign review.");
    } finally {
      setLoading(false);
    }
  }, [campaignId]);

  useEffect(() => {
    let isMounted = true;

    const run = async () => {
      if (!isMounted) return;
      await loadReview();
    };

    run();

    return () => {
      isMounted = false;
    };
  }, [loadReview]);

  const isPostRegistration = review?.serviceCommunicationType === "PostRegistration";
  const canQueue =
    canSendCommunications &&
    Boolean(review) &&
    !loading &&
    !queueing &&
    (isPostRegistration || (review?.recipientCount ?? 0) > 0) &&
    Boolean(review?.messageBody?.trim());

  const previewRecipients = review?.recipients ?? [];
  const selectedPreviewRecipient =
    previewRecipients.length > 0
      ? previewRecipients[Math.min(previewIndex, previewRecipients.length - 1)]
      : null;
  const selectedPreviewMessage =
    selectedPreviewRecipient?.personalisedMessage || review?.messageBody || "";

  const movePreview = (direction: -1 | 1) => {
    if (previewRecipients.length === 0) return;

    setPreviewIndex((current) => {
      const next = current + direction;

      if (next < 0) return previewRecipients.length - 1;
      if (next >= previewRecipients.length) return 0;

      return next;
    });
  };

  const queueCampaign = async () => {
    if (!campaignId || !review || queueing) return;

    const confirmed = window.confirm(
      isPostRegistration
        ? `Activate "${review.campaignName}" as the post-registration auto reply?`
        : `Queue "${review.campaignName}" for ${formatNumber(review.recipientCount)} recipients?`,
    );

    if (!confirmed) return;

    setQueueing(true);

    try {
      const response = await api<{ status: string }>(`/api/campaigns/${campaignId}/send`, {
        method: "POST",
      });
      notify.success(
        response.status?.toLowerCase() === "active"
          ? "Post-registration auto reply activated"
          : response.status?.toLowerCase() === "scheduled"
          ? "Campaign scheduled"
          : "Campaign queued for sending",
      );
      onQueued?.();
    } catch {
      notify.error("Failed to queue campaign");
    } finally {
      setQueueing(false);
    }
  };

  const recipientColumns = useMemo<GridColDef[]>(
    () => [
      {
        field: "displayName",
        headerName: "Recipient",
        flex: 1,
        minWidth: 180,
        valueGetter: (params) =>
          params.row.displayName ?? params.row.recipientName ?? "Unknown",
      },
      {
        field: "destination",
        headerName: "Mobile",
        flex: 1,
        minWidth: 150,
        valueGetter: (params) =>
          params.row.mobileNumber ??
          params.row.phoneNumber ??
          params.row.destination ??
          "",
      },
      {
        field: "estimatedSegments",
        headerName: "Segments",
        width: 100,
        align: "right",
        headerAlign: "right",
        valueGetter: (params) => params.row.estimatedSegments ?? 0,
      },
      {
        field: "warnings",
        headerName: "Issues",
        flex: 1,
        minWidth: 180,
        valueGetter: (params) => (params.row.warnings ?? []).join(", "),
      },
    ],
    [],
  );

  const rows =
    review?.recipients.map((recipient, index) => ({
      id: `${recipient.partyId}-${index}`,
      previewIndex: index,
      ...recipient,
    })) ?? [];

  return (
    <div className="page-container">
      <PageHeader
        title="Campaign Review"
        subtitle="Review recipients, message usage and validation before queueing."
        actions={
          <button
            type="button"
            className="icon-button"
            onClick={loadReview}
            disabled={loading || !campaignId}
            title="Refresh review"
          >
            <RefreshIcon fontSize="small" />
          </button>
        }
      />

      {error && <div className="notification notification--error">{error}</div>}

      {review && (
        <>
          <div className="form-row">
            <div className="card">
              <div className="card-title">Campaign Details</div>

              <div className="summary-section">
                <div className="summary-name">Name</div>
                <div className="summary-value">{review.campaignName}</div>
              </div>

              <div className="summary-section">
                <div className="summary-name">Sender</div>
                <div className="summary-value">
                  {review.senderName || "Default sender"}
                </div>
              </div>

              <div className="summary-section">
                <div className="summary-name">Campaign ID</div>
                <div className="summary-value">{review.campaignId}</div>
              </div>
            </div>

            <div className="card">
              <div className="card-title">SMS Estimate</div>

              <div className="summary-section">
                <div className="summary-name">Audience</div>
                <div className="summary-value">
                  {formatNumber(review.audienceCount)}
                </div>
              </div>

              <div className="summary-section">
                <div className="summary-name">Recipients</div>
                <div className="summary-value">
                  {formatNumber(review.recipientCount)}
                </div>
              </div>

              <div className="summary-section">
                <div className="summary-name">Segments</div>
                <div className="summary-value">
                  {formatNumber(review.estimatedSegments)}
                </div>
              </div>

              <div className="summary-section">
                <div className="summary-name">Messages</div>
                <div className="summary-value">
                  {formatNumber(review.estimatedMessages)}
                </div>
              </div>

              <div className="summary-section">
                <div className="summary-name">Estimated cost</div>
                <div className="summary-value">
                  {formatCurrency(review.estimatedCost)}
                </div>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-header-row">
              <div>
                <div className="card-title">Message Preview</div>
              </div>

              <div className="action-buttons">
                <button
                  type="button"
                  className="icon-button"
                  onClick={() => movePreview(-1)}
                  disabled={previewRecipients.length <= 1}
                  title="Previous recipient"
                >
                  <ChevronLeftIcon fontSize="small" />
                </button>
                <button
                  type="button"
                  className="icon-button"
                  onClick={() => movePreview(1)}
                  disabled={previewRecipients.length <= 1}
                  title="Next recipient"
                >
                  <ChevronRightIcon fontSize="small" />
                </button>
              </div>
            </div>

            {selectedPreviewRecipient && (
              <div className="summary-section">
                <div className="summary-name">
                  Recipient {formatNumber(previewIndex + 1)} of{" "}
                  {formatNumber(previewRecipients.length)}
                </div>
                <div className="summary-value">
                  {selectedPreviewRecipient.displayName ??
                    selectedPreviewRecipient.recipientName ??
                    "Unknown"}
                  {(selectedPreviewRecipient.mobileNumber ||
                    selectedPreviewRecipient.phoneNumber ||
                    selectedPreviewRecipient.destination) && (
                    <>
                      {" "}
                      |{" "}
                      {selectedPreviewRecipient.mobileNumber ??
                        selectedPreviewRecipient.phoneNumber ??
                        selectedPreviewRecipient.destination}
                    </>
                  )}
                </div>
              </div>
            )}

            <textarea
              className="form-input"
              value={selectedPreviewMessage}
              readOnly
              rows={5}
            />
          </div>

          <div className="card">
            <div className="card-header-row">
              <div>
                <div className="card-title">Validation</div>
              </div>
            </div>

            {review.warnings.length > 0 ? (
              <div className="option-group">
                {review.warnings.map((warning, index) => (
                  <div
                    key={`${warning}-${index}`}
                    className="notification notification--warning"
                  >
                    <WarningAmberIcon
                      fontSize="small"
                      style={{ verticalAlign: "text-bottom", marginRight: 6 }}
                    />
                    {warning}
                  </div>
                ))}
              </div>
            ) : (
              <div className="notification notification--success">
                <CheckCircleOutlineIcon
                  fontSize="small"
                  style={{ verticalAlign: "text-bottom", marginRight: 6 }}
                />
                No validation issues found.
              </div>
            )}
          </div>

          <div className="card">
            <div className="card-header-row">
              <div>
                <div className="card-title">
                  Recipients ({formatNumber(review.recipientCount)})
                </div>
              </div>
            </div>

            <div style={{ height: 420, marginTop: "0.5rem" }}>
              <DataGrid
                density="compact"
                loading={loading}
                rows={rows}
                columns={recipientColumns}
                pageSizeOptions={[10, 25, 50]}
                disableRowSelectionOnClick
                onRowClick={(params) =>
                  setPreviewIndex(params.row.previewIndex ?? 0)
                }
              />
            </div>
          </div>
        </>
      )}

      <div className="form-actions">
        <button
          type="button"
          className="form-submit-button form-submit-button--secondary"
          onClick={onBack}
        >
          <ArrowBackIcon fontSize="small" style={{ marginRight: 6 }} />
          Back
        </button>

        <button
          type="button"
          className="form-submit-button"
          onClick={queueCampaign}
          disabled={!canQueue}
        >
          <SendIcon fontSize="small" style={{ marginRight: 6 }} />
          {queueing ? (isPostRegistration ? "Activating..." : "Queueing...") : (isPostRegistration ? "Activate Auto Reply" : "Queue Campaign")}
        </button>
      </div>
    </div>
  );
}
